/**
 * Concessions Table Component
 * Displays concession request history with approval status
 */
import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import LoadingComponent from '../../loadingComponent';
import { StatusBadge } from './sharedComponents';
import moment from 'moment';

/**
 * Format date to DD/MM/YYYY using moment
 * @param {string} dateString - Date string in any format moment can parse
 * @returns {string} - Formatted date in DD/MM/YYYY format
 */
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = moment(dateString);
    return date.isValid() ? date.format('DD/MM/YYYY') : dateString;
};

/**
 * Render Concessions Table Component with API data
 * @param {Object} props - Component props
 * @param {Object} props.feeHistoryData - Fee history data from API (contains concessionHistory array)
 * @param {Boolean} props.isLoading - Loading state
 * @param {Object} props.studentData - Student data (optional)
 * @returns {JSX.Element} - Concessions table component
 */
export const ConcessionsTable = ({ feeHistoryData = null, isLoading = false, studentData = null }) => {
    const concessionHistoryData = feeHistoryData?.concessionHistory || [];

    const statusBodyTemplate = (rowData) => {
        if (!rowData || !rowData.requestStatus) return null;
        
        const statusStyles = {
            'Approved': {
                background: '#C8F4E3',
                color: '#065F46',
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontFamily: 'InterSemiBold'
            },
            'Pending': {
                background: '#FEF3C7',
                color: '#92400E',
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontFamily: 'InterSemiBold'
            },
            'Rejected': {
                background: '#FEE2E2',
                color: '#991B1B',
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontFamily: 'InterSemiBold'
            }
        };

        const style = statusStyles[rowData.requestStatus] || {
            background: '#E5E7EB',
            color: '#000000',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 16px',
            fontSize: '0.875rem',
            fontWeight: '500',
            display: 'inline-block'
        };

        return <span style={style}>{rowData.requestStatus}</span>;
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <div className="tab-content feetype-wise-content">
            <div style={{
                fontFamily: 'InterSemiBold',
                fontSize: '1rem',
                color: '#1F2937',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
            }}>
                <span>Concession History :</span>
                <div style={{ display: 'flex', gap: '1rem', marginLeft: 'auto' }}>
                    <span style={{
                        background: '#C8F4E3',
                        color: '#065F46',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}>
                        Approved
                    </span>
                    <span style={{
                        background: '#FEF3C7',
                        color: '#92400E',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}>
                        Pending
                    </span>
                    <span style={{
                        background: '#FEE2E2',
                        color: '#991B1B',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}>
                        Rejected
                    </span>
                </div>
            </div>

            <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                <DataTable
                    value={concessionHistoryData}
                    className="fee-details-datatable"
                    showGridlines
                    stripedRows
                    responsiveLayout="scroll"
                    emptyMessage="No concession history available"
                >
                    <Column
                        field="feeType"
                        header="FeeType"
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="feeAmount"
                        header="Fee Amount"
                        body={(rowData) => rowData?.feeAmount != null ? <span>{rowData.feeAmount.toFixed(2)}</span> : null}
                        style={{ minWidth: '10px' }}
                    />
                    <Column
                        field="requestStatus"
                        header="Request Status"
                        body={statusBodyTemplate}
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="concessionType"
                        header="Concession Type"
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="concessionRequestAmount"
                        header="Request Amount"
                        body={(rowData) => rowData?.concessionRequestAmount != null ? <span>{rowData.concessionRequestAmount.toFixed(2)}</span> : null}
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="requestFrom"
                        header="Request From"
                        style={{ minWidth: '130px' }}
                    />
                    <Column
                        field="requestRemarks"
                        header="Request Remarks"
                        style={{ minWidth: '160px' }}
                    />
                    <Column
                        field="requestDate"
                        header="Request Date"
                        body={(rowData) => formatDate(rowData?.requestDate)}
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        field="approvedBy"
                        header="Approved By"
                        style={{ minWidth: '140px' }}
                    />
                    <Column
                        field="approvalRemarks"
                        header="Approval Remarks"
                        style={{ minWidth: '160px' }}
                    />
                    <Column
                        field="approvedDate"
                        header="Approved Date"
                        body={(rowData) => formatDate(rowData?.approvedDate)}
                        style={{ minWidth: '180px' }}
                    />
                </DataTable>
            </div>
        </div>
    );
};
